import { FC, PropsWithChildren, useEffect, useState } from "react"
import BtButton from "../bt-button/bt-button"
import { useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router-dom";
import { ServiceWeeklyDigest, DigestChannels } from "../../services/service-weekly-digest";
import { ServiceSupplierInsuranceReminder } from "../../services/service-supplier-insurance-reminder";
import { ServiceSupplierProfileReminders } from "../../services/service-supplier-profile-reminders";
import { ServiceBidReceived, ServiceIntentReceived, ServiceMessageDigest } from "../../services/service-unsubscribe";
import LanguageSwitcher from "../../controls/LanguageSwitcher/LanguageSwitcher";
import styles from "./page-email-unsubscribe.module.scss";
import * as Sentry from "@sentry/react";
import axios from "axios";
import { Loading } from "../../components/shared";
import StyledCheckbox from "../../components/shared/StyledCheckbox/StyledCheckbox";

type Params = {
  p: string,
  s: string,
};

type RouteTypes = RouteComponentProps<Params, Record<string, unknown>>;

type UnsubscribeService = {
  unsubscribeEmailNotifications: (args: { p: string, s: string }) => Promise<unknown>;
  fetchStatus: (args: { p: string, s: string }) => Promise<boolean>;
}

type Props = {
  match: RouteTypes['match'];
  service: UnsubscribeService;
  description: string;
}

// The chrome every unsubscribe page shares. Extracted so the digest's two-choice page can
// reuse it without the single-button page changing what it renders.
const UnsubscribeShell: FC<PropsWithChildren<{ pageLoading: boolean, missingParams: boolean, failed?: boolean }>> = ({ pageLoading, missingParams, failed, children }) => {
  const [t] = useTranslation('shared', { keyPrefix: 'unsubscribe' });

  if (pageLoading) {
    return <Loading type="inline" />
  }

  return (
    <div className={styles.container}>
      <LanguageSwitcher className={styles.languageSwitcher} />
      <div className={styles.content}>
        {missingParams && <h2>{t('title.missingParams', 'Missing parameters (p, s)')}</h2>}
        {/* Without this the page renders its controls over state it never loaded, which reads
            as "you already receive nothing" for a mail that just arrived. */}
        {!missingParams && failed && (
          <>
            <h2>{t('title.failed', 'This link no longer works')}</h2>
            <p>{t('description.failed', 'The link may have expired. Sign in and change your notification settings on your profile instead.')}</p>
          </>
        )}
        {!missingParams && !failed && children}
      </div>
    </div>
  )
}

export const PageEmailUnsubscribe: FC<Props> = ({ match, service, description }) => {
  const [t] = useTranslation('shared', { keyPrefix: 'unsubscribe' });
  const [pageLoading, setPageLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [missingParams, setMissingParams] = useState(false);
  const { p, s } = match.params;

  useEffect(() => {
    (async() => {
      try {
        const status = await service.fetchStatus({ p, s });
        setSubscribed(status !== false);
      } catch (e) {
        if (axios.isAxiosError(e)) {
          if (e.response?.status !== 400) {
            Sentry.captureException(e);
          } else if (e.response?.data?.error === 'error.missing.parameters') {
            setMissingParams(true);
          }
        }
      } finally {
        setPageLoading(false);
      }
    })();
  }, []);

  const unsubscribe = async() => {
    setLoading(true);
    try {
      await service.unsubscribeEmailNotifications({ p, s });
      setSubscribed(false);
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status !== 400) {
        Sentry.captureException(e);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <UnsubscribeShell pageLoading={pageLoading} missingParams={missingParams}>
      {subscribed ? (
        <>
          <h2>{t('button.subscribed.text', 'Unsubscribe')}</h2>
          <p>{description}</p>
          <BtButton loaderShow={loading} onClick={unsubscribe}>{t('button.text', 'Unsubscribe')}</BtButton>
        </>
        ) : (
        <>
          <h2>{t('button.unsubscribed.text', 'You\'ve been unsubscribed')}</h2>
          <p>{t('description.unsubscribed', 'You will no longer receive this email from us. If this was a mistake, please login and change your preferences in your account.')}</p>
          <BtButton onClick={() => {
            window.location.href = '/';
          }}>{t('button.returnHome', 'Back to Accurator')}</BtButton>
        </>
      )}
    </UnsubscribeShell>
  )
}

// Per-email-type wrappers used by the router.

type RouteOnlyProps = { match: RouteTypes['match'] };

// The digest is one mail with two sections, so its page offers the choice rather than a
// single button: someone who only wants the public half to stop should not lose the rest.
export const PageWeeklyDigestUnsubscribe: FC<RouteOnlyProps> = ({ match }) => {
  const [t] = useTranslation('shared', { keyPrefix: 'unsubscribe' });
  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [missingParams, setMissingParams] = useState(false);
  const [failed, setFailed] = useState(false);
  const [channels, setChannels] = useState<DigestChannels>({});
  // What the server had when the page opened. `channels` is the edit in progress, so reading
  // disabled off it locks a box the moment it is un-ticked, with nothing saved yet.
  const [fetched, setFetched] = useState<DigestChannels>({});
  const { p, s } = match.params;

  useEffect(() => {
    (async () => {
      try {
        const current = await ServiceWeeklyDigest.fetchChannels({ p, s });
        const asBooleans = {
          weeklyDigest: current.weeklyDigest === true,
          weeklyDigestPublic: current.weeklyDigestPublic === true,
        };
        setChannels(asBooleans);
        setFetched(asBooleans);
      } catch (e) {
        if (axios.isAxiosError(e) && e.response?.status === 400) {
          if (e.response?.data?.error === 'error.missing.parameters') setMissingParams(true);
          else setFailed(true);
        } else {
          Sentry.captureException(e);
          setFailed(true);
        }
      } finally {
        setPageLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      // The reply carries what now applies, so the confirmation cannot overstate it.
      setChannels(await ServiceWeeklyDigest.updateChannels({ p, s, channels }));
      setSaved(true);
    } catch (e) {
      if (!axios.isAxiosError(e) || e.response?.status !== 400) Sentry.captureException(e);
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  const toggle = (key: keyof DigestChannels) => setChannels((current) => ({ ...current, [key]: !current[key] }));

  return (
    <UnsubscribeShell pageLoading={pageLoading} missingParams={missingParams} failed={failed}>
      {saved ? (
        <>
          <h2>{t('title.digestSaved', 'Your choice is saved')}</h2>
          <p>{t('description.digestSaved', 'You will receive exactly the sections you left switched on.')}</p>
          <BtButton onClick={() => { window.location.href = '/'; }}>{t('button.returnHome', 'Back to Accurator')}</BtButton>
        </>
      ) : (
        <>
          <h2>{t('title.digest', 'Weekly digest')}</h2>
          <p>{t('description.digest', 'Choose which parts of the weekly email you want to keep receiving.')}</p>
          <label className={styles.channel}>
            <StyledCheckbox
              checked={channels.weeklyDigest === true}
              disabled={fetched.weeklyDigest !== true}
              onChange={() => toggle('weeklyDigest')}
            />
            <span>{t('channel.weeklyDigest', 'Requests on the Accurator marketplace')}</span>
          </label>
          <label className={styles.channel}>
            <StyledCheckbox
              checked={channels.weeklyDigestPublic === true}
              disabled={fetched.weeklyDigestPublic !== true}
              onChange={() => toggle('weeklyDigestPublic')}
            />
            <span>{t('channel.weeklyDigestPublic', 'Public tenders')}</span>
          </label>
          <BtButton loaderShow={saving} onClick={save}>{t('button.saveChoice', 'Save')}</BtButton>
          <p>{t('description.turnBackOn', 'To start receiving a section again, sign in and change it on your profile.')}</p>
        </>
      )}
    </UnsubscribeShell>
  );
}

export const PageSupplierInsuranceReminderUnsubscribe: FC<RouteOnlyProps> = (props) => {
  const [t] = useTranslation('shared', { keyPrefix: 'unsubscribe' });
  return (
    <PageEmailUnsubscribe
      {...props}
      service={ServiceSupplierInsuranceReminder}
      description={t('description.supplierInsuranceReminder', "We're sorry to see you go. Click the button below to stop receiving insurance expiry reminders from Accurator.")}
    />
  );
}

export const PageSupplierProfileReminderUnsubscribe: FC<RouteOnlyProps> = (props) => {
  const [t] = useTranslation('shared', { keyPrefix: 'unsubscribe' });
  return (
    <PageEmailUnsubscribe
      {...props}
      service={ServiceSupplierProfileReminders}
      description={t('description.supplierProfileReminder', "We're sorry to see you go. Click the button below to stop receiving profile completion reminders from Accurator.")}
    />
  );
}

export const PageBidReceivedUnsubscribe: FC<RouteOnlyProps> = (props) => {
  const [t] = useTranslation('shared', { keyPrefix: 'unsubscribe' });
  return (
    <PageEmailUnsubscribe
      {...props}
      service={ServiceBidReceived}
      description={t('description.bidReceived', "You can manage which notifications you receive at any time from your profile page in Accurator. To stop receiving emails when a supplier submits a bid in your projects, click the button below.")}
    />
  );
}

export const PageIntentReceivedUnsubscribe: FC<RouteOnlyProps> = (props) => {
  const [t] = useTranslation('shared', { keyPrefix: 'unsubscribe' });
  return (
    <PageEmailUnsubscribe
      {...props}
      service={ServiceIntentReceived}
      description={t('description.intentReceived', "You can manage which notifications you receive at any time from your profile page in Accurator. To stop receiving emails when a supplier declares their intent to bid, click the button below.")}
    />
  );
}

export const PageMessageDigestUnsubscribe: FC<RouteOnlyProps> = (props) => {
  const [t] = useTranslation('shared', { keyPrefix: 'unsubscribe' });
  return (
    <PageEmailUnsubscribe
      {...props}
      service={ServiceMessageDigest}
      description={t('description.messageDigest', "You can manage which notifications you receive at any time from your profile page in Accurator. To stop receiving the daily summary of new supplier messages, click the button below.")}
    />
  );
}
